// --------------------------------------------------------------
// Created On: 2023-07-20
// Author: Zachary Thomas
//
// Last Modified: 2025-04-09
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, Fragment } from "react";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import PropTypes from "prop-types";
import {
  CONTROLLER_APP_VFD,
  CONTROLLER_APP_SOFT_START,
  CONTROLLER_APP_MODBUS_SENSOR,
  CONTROLLER_WORLDWIDE_SOFT_START,
  CONTROLLER_ACS580,
  CONTROLLER_ACS550,
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
  CONTROLLER_CORNELL_VALVE,
} from "../../../constants/miscellaneous";
import { LOCK_REMOTE_CONTROL_PERMISSION } from "../../../constants/permissions";
import styles from "./RemoteOperationPanel.module.scss";
import userHasPermission from "../../../utilities/userHasPermission";
import InfoModal from "../../../components/InfoModal/InfoModal";
import RemoteOperationDisclaimer from "./RemoteOperationDisclaimer/RemoteOperationDisclaimer";
import CommunicationModeControls from "./CommunicationModeControls/CommunicationModeControls";
import RunningStatusControls from "./RunningStatusControls/RunningStatusControls";
import SpeedChangeControls from "./SpeedChangeControls/SpeedChangeControls";
import ManualRpmControls from "./ManualRpmControls/ManualRpmControls";
import MonitoringDeviceOperationControls from "./MonitoringDeviceOperationControls/MonitoringDeviceOperationControls";
import RemoteLockControls from "./RemoteLockControls/RemoteLockControls";
import ValvePositionControls from "./ValvePositionControls/ValvePositionControls";

// Panel for remotely operating an asset.
export default function RemoteOperationPanel(props: Props): Component {
  const windowDimensions = useWindowDimensions();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);

  return (
    <div className={styles.body}>
      <div className={styles.titleBar}>
        <label className={styles.title}>{props.assetName} Remote Operation</label>
      </div>

      {/* Many remote operations require a controller. */}
      {props.controllerModel !== null && (
        <Fragment>
          {/* Special controls for the ACS580 and ACS550 VFDs. */}
          {[CONTROLLER_ACS580, CONTROLLER_ACS550].includes(props.controllerModel) && (
            <CommunicationModeControls
              modes={[
                {
                  value: "Modbus Control",
                  label: "Modbus",
                },
                {
                  value: "Analog/Digital Control",
                  label: "Analog/Digital",
                },
              ]}
              onChangeCommunicationMode={(communicationMode) => props.onChangeCommunicationMode(communicationMode)}
            />
          )}

          {/* Special controls for the Worldwide Soft Start. */}
          {props.controllerModel === CONTROLLER_WORLDWIDE_SOFT_START && (
            <CommunicationModeControls
              modes={[
                {
                  value: "Remote",
                  label: "Remote",
                },
                {
                  value: "Local",
                  label: "Local",
                },
              ]}
              onChangeCommunicationMode={(communicationMode) => props.onChangeCommunicationMode(communicationMode)}
            />
          )}

          {props.controllerApplicationType !== CONTROLLER_APP_MODBUS_SENSOR &&
            props.controllerModel !== CONTROLLER_CORNELL_VALVE && (
              <RunningStatusControls
                controllerModel={props.controllerModel}
                loading={props.loading}
                onChangeRunningStatus={(runningStatus) => props.onChangeRunningStatus(runningStatus)}
              />
            )}

          {/* Soft starts / modbus sensors do not support speed change. */}
          {props.controllerApplicationType !== CONTROLLER_APP_SOFT_START &&
            props.controllerApplicationType !== CONTROLLER_APP_MODBUS_SENSOR &&
            props.controllerModel !== CONTROLLER_CORNELL_VALVE && (
              <SpeedChangeControls
                loading={props.loading}
                defaultSpeeds={
                  props.controllerApplicationType === CONTROLLER_APP_VFD
                    ? [1000, 1200, 1300, 1400, 1500, 1600, 1800, 2000]
                    : [800, 1000, 1200, 1300, 1400, 1500, 1600, 1800]
                }
                onChangeSpeed={(rpm) => props.onChangeSpeed(rpm)}
              />
            )}

          {/* Soft starts / modbus sensors do not support speed change. */}
          {props.controllerApplicationType !== CONTROLLER_APP_SOFT_START &&
            props.controllerApplicationType !== CONTROLLER_APP_MODBUS_SENSOR &&
            props.controllerModel !== CONTROLLER_CORNELL_VALVE && (
              <ManualRpmControls
                loading={props.loading}
                windowDimensionWidth={windowDimensions.width}
                onChangeSpeed={(command) => props.onChangeSpeed(command)}
                onError={(error) => props.onError(error)}
              />
            )}
        </Fragment>
      )}

      {props.controllerModel === CONTROLLER_CORNELL_VALVE && (
        <ValvePositionControls
          valves={[1, 2, 3, 4]}
          positions={[0, 25, 50, 100]}
          windowDimensionWidth={windowDimensions.width}
          loading={props.loading}
          onChangeValvePosition={(valveNumber, percentOpen) => props.onChangeValvePosition(valveNumber, percentOpen)}
          onError={(error) => props.onError(error)}
        />
      )}

      {DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(props.deviceType || "") && (
        <MonitoringDeviceOperationControls
          deviceType={props.deviceType || ""}
          loading={props.loading}
          windowDimensionWidth={windowDimensions.width}
          onClearFailedCommands={() => props.onClearFailedCommands()}
          onRestartMonitoringDevice={() => props.onRestartMonitoringDevice()}
          onReapplyConfiguration={() => props.onReapplyConfiguration()}
          onBlinkLeds={() => props.onBlinkLeds()}
          onUpdateFirmware={() => props.onUpdateFirmware()}
        />
      )}

      {userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]) && (
        <RemoteLockControls
          isLockedOut={props.isLockedOut}
          loading={props.loading}
          windowDimensionWidth={windowDimensions.width}
          onChangeLock={(isLocked) => props.onChangeLock(isLocked)}
        />
      )}

      <label className={styles.controlLabel}>
        <InfoModal
          showModal={showDisclaimer}
          title="Remote Operation Disclaimer"
          linkMessage="(Remote Operation Disclaimer)"
          darkBackground={true}
          onClick={() => setShowDisclaimer(true)}
          onClose={() => setShowDisclaimer(false)}
        >
          <RemoteOperationDisclaimer />
        </InfoModal>
      </label>
    </div>
  );
}

RemoteOperationPanel.propTypes = {
  assetName: PropTypes.string.isRequired,
  controllerApplicationType: PropTypes.string,
  controllerModel: PropTypes.string,
  deviceType: PropTypes.string,
  isLockedOut: PropTypes.bool.isRequired,
  canUnlock: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onChangeCommunicationMode: PropTypes.func.isRequired,
  onChangeSpeed: PropTypes.func.isRequired,
  onChangeRunningStatus: PropTypes.func.isRequired,
  onClearFailedCommands: PropTypes.func.isRequired,
  onRestartMonitoringDevice: PropTypes.func.isRequired,
  onReapplyConfiguration: PropTypes.func.isRequired,
  onBlinkLeds: PropTypes.func.isRequired,
  onChangeLock: PropTypes.func.isRequired,
  onUpdateFirmware: PropTypes.func.isRequired,
  onChangeValvePosition: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

interface Props {
  assetName: string;
  controllerApplicationType: string | null;
  controllerModel: string | null;
  deviceType: string | null;
  isLockedOut: boolean;
  canUnlock: boolean;
  loading: boolean;
  onChangeCommunicationMode: (command: string) => void;
  onChangeSpeed: (command: number) => void;
  onChangeRunningStatus: (command: string) => void;
  onClearFailedCommands: () => void;
  onRestartMonitoringDevice: () => void;
  onReapplyConfiguration: () => void;
  onBlinkLeds: () => void;
  onChangeLock: (isLocked: boolean) => void;
  onUpdateFirmware: () => void;
  onChangeValvePosition: (valveNumber: number, percentOpen: number) => void;
  onError: (error: string) => void;
}
